import React, { useCallback, useEffect, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import SettingsPageHeader from 'components/SettingsPageHeader';
import ListEmptyState from 'components/condition-list/ListEmptyState';
import {
  ChecklistResult,
  RequestType,
  createChecklist,
  deleteChecklist,
  listChecklist,
  reorderChecklist,
  updateChecklist,
} from 'stores/ContractRequestChecklistStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  CREATE: '新規',
  CHANGE: '変更',
  TERMINATE: '解除',
};

const REQUEST_TYPES: RequestType[] = ['CREATE', 'CHANGE', 'TERMINATE'];

const ChecklistManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ChecklistResult[]>([]);
  const [tabIndex, setTabIndex] = useState(0);

  const currentRequestType = REQUEST_TYPES[tabIndex];

  const fetchItems = useCallback(() => {
    setLoading(true);
    listChecklist({ includeDeleted: true })
      .then((results) => {
        setItems(results);
        setLoading(false);
      })
      .catch(() => {
        errorDefault('チェックリストの取得に失敗しました');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const activeItems = items
    .filter((item) => item.requestType === currentRequestType && !item.isDeleted)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const deletedItems = items.filter(
    (item) => item.requestType === currentRequestType && item.isDeleted,
  );

  return (
    <Box sx={{ p: 1.5, height: '100%', overflow: 'auto' }}>
      <SettingsPageHeader
        breadcrumbs={[{ label: '契約管理', to: '/contract' }, { label: 'チェックリスト設定' }]}
        icon={<ChecklistIcon />}
        title="作業チェックリスト"
        description="申請種別ごとに、承認前に確認するチェック項目を設定します"
      />

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          {REQUEST_TYPES.map((type) => (
            <Tab key={type} label={REQUEST_TYPE_LABELS[type]} sx={{ textTransform: 'none' }} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={0}>
              <ChecklistItems
                items={activeItems}
                requestType={currentRequestType}
                onUpdate={(updatedItem) => {
                  setItems((prev) =>
                    prev.map((i) => (i.checklistId === updatedItem.checklistId ? updatedItem : i)),
                  );
                }}
                onDelete={(deletedId) => {
                  setItems((prev) =>
                    prev.map((i) => (i.checklistId === deletedId ? { ...i, isDeleted: true } : i)),
                  );
                }}
                onReorder={(reordered) => {
                  setItems((prev) => {
                    const otherItems = prev.filter(
                      (i) => i.requestType !== currentRequestType || i.isDeleted,
                    );
                    return [...otherItems, ...reordered];
                  });
                }}
                onCreate={(newItem) => {
                  setItems((prev) => [...prev, newItem]);
                }}
              />

              {deletedItems.length > 0 && <DeletedSection items={deletedItems} />}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// --- Active Checklist Items ---

type ChecklistItemsProps = {
  items: ChecklistResult[];
  requestType: RequestType;
  onUpdate: (item: ChecklistResult) => void;
  onDelete: (checklistId: string) => void;
  onReorder: (items: ChecklistResult[]) => void;
  onCreate: (item: ChecklistResult) => void;
};

const ChecklistItems: React.FC<ChecklistItemsProps> = ({
  items,
  requestType,
  onUpdate,
  onDelete,
  onReorder,
  onCreate,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ChecklistResult | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const addInputRef = useRef<HTMLInputElement>(null);

  // --- Edit handlers ---

  const handleStartEdit = (item: ChecklistResult) => {
    setEditingId(item.checklistId);
    setEditValue(item.label);
  };

  const handleSaveEdit = (item: ChecklistResult) => {
    if (!editValue.trim() || editValue === item.label) {
      setEditingId(null);
      return;
    }
    updateChecklist({ checklistId: item.checklistId, label: editValue.trim() })
      .then(() => {
        onUpdate({ ...item, label: editValue.trim() });
        setEditingId(null);
        successDefault('更新しました');
      })
      .catch((e: Error) => {
        errorDefault('更新に失敗しました', e.message);
      });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  // --- Add handlers ---

  const handleStartAdd = () => {
    setIsAdding(true);
    setAddValue('');
    setTimeout(() => addInputRef.current?.focus(), 50);
  };

  const handleSaveAdd = () => {
    if (!addValue.trim()) {
      setIsAdding(false);
      return;
    }
    const sortOrder = items.length > 0 ? Math.max(...items.map((i) => i.sortOrder)) + 1 : 0;
    createChecklist({ label: addValue.trim(), requestType, sortOrder })
      .then((checklistId) => {
        onCreate({
          checklistId,
          label: addValue.trim(),
          sortOrder,
          requestType,
          isDeleted: false,
        });
        setIsAdding(false);
        setAddValue('');
        successDefault('登録しました');
      })
      .catch((e: Error) => {
        errorDefault('登録に失敗しました', e.message);
      });
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setAddValue('');
  };

  // --- Delete handlers ---

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteChecklist({ checklistId: deleteTarget.checklistId })
      .then(() => {
        onDelete(deleteTarget.checklistId);
        setDeleteTarget(null);
        successDefault('削除しました');
      })
      .catch((e: Error) => {
        errorDefault('削除に失敗しました', e.message);
        setDeleteTarget(null);
      });
  };

  // --- Drag & Drop handlers ---

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const isAbove = e.clientY < midY;
    setDragOverIndex(isAbove ? index : index + 1);
  };

  const handleDrop = () => {
    if (dragIndex === null || dragOverIndex === null) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    let targetIndex = dragOverIndex;
    if (targetIndex > dragIndex) {
      targetIndex -= 1;
    }

    if (dragIndex === targetIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const updated = reordered.map((item, i) => ({ ...item, sortOrder: i }));

    onReorder(updated);

    reorderChecklist({
      items: updated.map((item) => ({
        checklistId: item.checklistId,
        sortOrder: item.sortOrder,
      })),
    }).catch((e: Error) => {
      errorDefault('並び替えの保存に失敗しました', toErrorDetail(e));
    });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      <Stack spacing={0}>
        {items.length === 0 && !isAdding && (
          <ListEmptyState
            icon={<ChecklistIcon sx={{ fontSize: 24 }} />}
            title="チェック項目がありません"
            description="下の「新しい項目を追加」から、この申請種別のチェック項目を登録してください。"
          />
        )}
        {items.map((item, index) => (
          <React.Fragment key={item.checklistId}>
            {/* Insertion indicator line */}
            {dragOverIndex === index && dragIndex !== null && dragIndex !== index && (
              <Box
                sx={{
                  height: 2,
                  bgcolor: 'primary.main',
                  borderRadius: 1,
                  mx: 1,
                }}
              />
            )}
            <Box
              draggable={editingId !== item.checklistId}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop()}
              onDragEnd={handleDragEnd}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.75,
                borderRadius: 1,
                opacity: dragIndex === index ? 0.4 : 1,
                transition: 'opacity 0.2s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <DragIndicatorIcon
                sx={{
                  fontSize: 20,
                  color: 'text.disabled',
                  cursor: 'grab',
                  mr: 1,
                  flexShrink: 0,
                }}
              />

              {editingId === item.checklistId ? (
                <TextField
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(item);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  onBlur={() => handleSaveEdit(item)}
                  autoFocus
                  fullWidth
                  sx={{ mr: 1 }}
                  inputProps={{ maxLength: 200 }}
                />
              ) : (
                <Typography
                  variant="body2"
                  onClick={() => handleStartEdit(item)}
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  {item.label}
                </Typography>
              )}

              <IconButton
                size="small"
                onClick={() => setDeleteTarget(item)}
                sx={{ color: 'text.disabled', flexShrink: 0 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </React.Fragment>
        ))}
        {/* Insertion indicator at the end */}
        {dragOverIndex === items.length && dragIndex !== null && (
          <Box
            sx={{
              height: 2,
              bgcolor: 'primary.main',
              borderRadius: 1,
              mx: 1,
            }}
          />
        )}

        {/* Add new item */}
        {isAdding ? (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75 }}>
            <Box sx={{ width: 28 }} />
            <TextField
              inputRef={addInputRef}
              size="small"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveAdd();
                if (e.key === 'Escape') handleCancelAdd();
              }}
              onBlur={() => {
                if (!addValue.trim()) handleCancelAdd();
              }}
              placeholder="項目名を入力"
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
        ) : (
          <Button
            startIcon={<AddIcon />}
            onClick={handleStartAdd}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              px: 1,
              py: 0.75,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            新しい項目を追加
          </Button>
        )}
      </Stack>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
        }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={600}>
            削除しますか？
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            「{deleteTarget?.label}
            」を削除します。削除済み項目として残りますが、新しい申請には紐付けられなくなります。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Deleted Section ---

type DeletedSectionProps = {
  items: ChecklistResult[];
};

const DeletedSection: React.FC<DeletedSectionProps> = ({ items }) => {
  return (
    <Accordion
      elevation={0}
      defaultExpanded={false}
      sx={{
        mt: 2,
        '&:before': { display: 'none' },
        border: 0,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 36 }}>
        <Typography variant="body2" color="text.secondary">
          削除済み項目 ({items.length}件)
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, pt: 0 }}>
        <Stack spacing={0}>
          {items.map((item) => (
            <Box
              key={item.checklistId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.75,
              }}
            >
              <Box sx={{ width: 28 }} />
              <Typography variant="body2" color="text.disabled">
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ChecklistManagement;
